import type { MetaDescriptor } from 'react-router'
import { DEFAULT_LOCALE, LOCALES, messages, type Locale } from './i18n/messages'
import { localePath, stripLocale } from './i18n/localePath'
import { SITE_NAME } from '@/site.config'

/** Suffix of every <title> and the og:site_name. */
export const BASE_TITLE = SITE_NAME

/** What a route loader knows about its page, for the <head>. */
export type PageSeo = {
  title?: string
  description?: string
  image?: string
  type?: 'website' | 'article'
  noindex?: boolean
  jsonLd?: Record<string, unknown>
}

/** Site-wide SEO defaults (SiteSettings in the CMS), from the locale layout. */
export type SeoDefaults = {
  title?: string
  description?: string
  image?: string
}

type MatchLike = { id: string; loaderData?: unknown } | undefined

type SeoContext = {
  siteUrl: string
  indexable: boolean
  locale: Locale
  defaults: SeoDefaults
  /** The locale layout matched an unsupported `/:lang` prefix (a 404). */
  unknownPrefix: boolean
}

function seoContext(matches: MatchLike[]): SeoContext {
  const root = matches.find((m) => m?.id === 'root')?.loaderData as
    | { siteUrl: string; indexable: boolean }
    | undefined
  const layout = matches.find(
    (m) => (m?.loaderData as { locale?: unknown } | undefined)?.locale !== undefined,
  )?.loaderData as { locale: Locale; seoDefaults: SeoDefaults; unknownPrefix?: boolean } | undefined
  return {
    siteUrl: root?.siteUrl ?? '',
    indexable: root?.indexable ?? true,
    locale: layout?.locale ?? DEFAULT_LOCALE,
    defaults: layout?.seoDefaults ?? {},
    unknownPrefix: layout?.unknownPrefix ?? false,
  }
}

/** Open Graph locale (`da_DK`) for a content locale code. */
const OG_LOCALE: Record<string, string> = {
  da: 'da_DK', en: 'en_US', sv: 'sv_SE', nb: 'nb_NO', nn: 'nn_NO', no: 'nb_NO',
  de: 'de_DE', fr: 'fr_FR', es: 'es_ES', it: 'it_IT', nl: 'nl_NL', fi: 'fi_FI', pl: 'pl_PL',
}

export function ogLocale(locale: string): string {
  const known = OG_LOCALE[locale]
  if (known) return known
  const [lang, region] = locale.split(/[-_]/)
  return region ? `${lang}_${region.toUpperCase()}` : lang
}

/**
 * The single place that turns page SEO data into head tags: title,
 * description, canonical, hreflang alternates for every locale, Open Graph,
 * Twitter and optional JSON-LD. Used by every route's `meta` export.
 */
export function buildMeta(
  { matches, location }: { matches: MatchLike[]; location: { pathname: string } },
  page: PageSeo = {},
): MetaDescriptor[] {
  const { siteUrl, indexable, locale, defaults, unknownPrefix } = seoContext(matches)
  // `/foobar` matches the `/:lang` tree (as the home route) but is a 404: the
  // page's own SEO must not leak onto it.
  if (unknownPrefix) page = { title: messages[DEFAULT_LOCALE].pageTitles.notFound, noindex: true }
  const barePath = stripLocale(location.pathname)
  const abs = (loc: Locale) => `${siteUrl}${localePath(barePath, loc)}`

  const resolvedTitle = page.title || defaults.title || BASE_TITLE
  const title = resolvedTitle.includes(BASE_TITLE) ? resolvedTitle : `${resolvedTitle} — ${BASE_TITLE}`
  const description = page.description || defaults.description
  const imageRaw = page.image || defaults.image
  const image = imageRaw && !imageRaw.startsWith('http') ? `${siteUrl}${imageRaw}` : imageRaw
  const noindex = page.noindex || !indexable

  const tags: MetaDescriptor[] = [
    { title },
    { tagName: 'link', rel: 'canonical', href: abs(locale) },
    ...LOCALES.map((loc) => ({ tagName: 'link', rel: 'alternate', hrefLang: loc, href: abs(loc) })),
    { tagName: 'link', rel: 'alternate', hrefLang: 'x-default', href: abs(DEFAULT_LOCALE) },
    { property: 'og:title', content: title },
    { property: 'og:type', content: page.type ?? 'website' },
    { property: 'og:site_name', content: BASE_TITLE },
    { property: 'og:url', content: abs(locale) },
    { property: 'og:locale', content: ogLocale(locale) },
    { name: 'twitter:card', content: image ? 'summary_large_image' : 'summary' },
    { name: 'twitter:title', content: title },
  ]
  if (description) {
    tags.push(
      { name: 'description', content: description },
      { property: 'og:description', content: description },
      { name: 'twitter:description', content: description },
    )
  }
  if (image) tags.push({ property: 'og:image', content: image }, { name: 'twitter:image', content: image })
  if (noindex) tags.push({ name: 'robots', content: 'noindex' })
  if (page.jsonLd) tags.push({ 'script:ld+json': page.jsonLd })
  return tags
}
